/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { AuthenticationResult } from "@azure/msal-browser";
import { useEffect, useRef } from "react";

import { error } from "../../../util/log";

export type AcquireTokenCallback = () => Promise<AuthenticationResult | null>;

/**
 * Re-acquires the token whenever the tab regains focus, so a tab left idle picks MSAL's renewed
 * ID token up without the user having to navigate or reload.
 *
 * An account switch does not need this — it drives the Axios request interceptor through
 * `getAccounts`/`getAccountDetails`, and the interceptor already acquires. An idle tab fires no
 * request at all: `usePermissionsQuery` builds its own Axios client, so even a permissions refetch
 * never reaches the interceptor. Focus is the only thing left to trigger the renewal.
 *
 * This adds no traffic of its own. `acquireTokenSilent` serves MSAL's cache until renewal is due,
 * so the common case is a cache read and an unchanged token that `setAccountIdToken` ignores.
 *
 * A failure here is logged and dropped rather than signing the user out: unlike the acquisition at
 * startup, the app is already running with a usable token, so a transient failure on focus must not
 * cost the user their session. Swallowing it also keeps the rejection off `window.unhandledrejection`.
 */
export const useAcquireTokenOnFocus = (acquireToken: AcquireTokenCallback) => {
  const latestAcquireToken = useRef(acquireToken);
  latestAcquireToken.current = acquireToken;

  useEffect(() => {
    const refreshToken = () => {
      latestAcquireToken.current().catch((err) => {
        error("Error acquiring token on focus:", err);
      });
    };

    const refreshTokenWhenVisible = () => {
      if (document.visibilityState === "visible") {
        refreshToken();
      }
    };

    window.addEventListener("focus", refreshToken);
    document.addEventListener("visibilitychange", refreshTokenWhenVisible);

    return () => {
      window.removeEventListener("focus", refreshToken);
      document.removeEventListener("visibilitychange", refreshTokenWhenVisible);
    };
  }, []);
};
